import { useEffect, useMemo, useState } from 'react';
import DefaultLayout from '../../layout/DefaultLayout';
import UniversalRecordTable from '../../components/Tables/universalRecord/UniversalRecordTable';
import CustomerRegistrationPopup from '../../components/Customer/CustomerRegistrationPopup';
import CustomerEditPopup from '../../components/Customer/CustomerEditPopup';
import CustomerViewPopup from '../../components/Customer/CustomerViewPopup';
import { customerColumns } from '../../features/customer/customerColumns';
import { useCustomer } from '../../hooks/useCustomer';
import { buildCustomerFilters } from '../../features/customer/buildCustomerFilters';
import BreadcrumbTwo from '../../components/Breadcrumbs/BreadcrumbTwo';
import { FilterConfig } from '../../components/Tables/advancedRecord/FilterBar';

const CustomerPage = () => {
  const {
    list,
    paginator,
    fetchCustomersPaged,
    fetchFilteredCustomers,
    handlerClearFilteredCustomers,
    loadCustomerById,
    loadCustomerForEdit,
    showRegistrationModal,
    openAddModal,
    closeAddModal,
    toggleStatus,
    saveCustomer,
    showViewModal,
    closeViewModal,
    showEditModal,
    closeEditModal,
  } = useCustomer();

  const [filters, setFilters] = useState<any>({});
  const [filtersApplied, setFiltersApplied] = useState(false);

  const pageSize = paginator?.size || 10;
  const currentPage = paginator?.number || 0;

  useEffect(() => {
    fetchCustomersPaged(currentPage, pageSize);
  }, []);

  const columns = useMemo(() => customerColumns, []);

  const filterFields: FilterConfig[] = useMemo(
    () => buildCustomerFilters(),
    []
  );

  // 🔧 Construye el payload en base a los filtros que llegan del componente
  const buildCustomerFilterPayload = (rawFilters: any) => {
    const payload: any = {};

    if (rawFilters.name) payload.name = rawFilters.name;
    if (rawFilters.nit) payload.nit = rawFilters.nit;
    if (rawFilters.nrc) payload.nrc = rawFilters.nrc;
    if (rawFilters.dui) payload.dui = rawFilters.dui;
    if (rawFilters.passportNumber)
      payload.passportNumber = rawFilters.passportNumber;
    if (rawFilters.residentCardNumber)
      payload.residentCardNumber = rawFilters.residentCardNumber;
    if (rawFilters.otherIdentification)
      payload.otherIdentification = rawFilters.otherIdentification;
    if (rawFilters.legalName) payload.legalName = rawFilters.legalName;
    if (rawFilters.tradeName) payload.tradeName = rawFilters.tradeName;
    if (rawFilters.email) payload.email = rawFilters.email;

    return payload;
  };

  // 📌 Evento cuando aplicas filtros
  const handleFilterChange = (newFilters: any) => {
    const payload = buildCustomerFilterPayload(newFilters);
    setFilters(payload);
    setFiltersApplied(true);
    fetchFilteredCustomers(payload, 0, pageSize);
  };

  // 📌 Evento al cambiar de página
  const handlePageChange = (page: number) => {
    if (filtersApplied) {
      fetchFilteredCustomers(filters, page, pageSize);
    } else {
      fetchCustomersPaged(page, pageSize);
    }
  };

  // 📌 Evento al cambiar tamaño de página
  const handlePageSizeChange = (size: number) => {
    if (filtersApplied) {
      fetchFilteredCustomers(filters, 0, size);
    } else {
      fetchCustomersPaged(0, size);
    }
  };

  const getActions = (row: any) => {
    return [
      {
        key: 'view',
        label: 'Ver',
        onClick: async () => {
          await loadCustomerById(row.id);
        },
      },
      {
        key: 'edit',
        label: 'Editar',
        onClick: async () => {
          await loadCustomerForEdit(row.id);
        },
      },
      {
        key: 'cancel',
        label: row.status ? 'Desactivar' : 'Activar',
        onClick: async () => {
          await toggleStatus(row.id);
        },
      },
    ];
  };

  return (
    <DefaultLayout>
      <BreadcrumbTwo />

      <UniversalRecordTable
        columns={columns}
        data={list}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        paginator={paginator}
        showPagination={true}
        enableRowClick={true}
        pageSize={pageSize}
        noDataMessage="Utiliza los filtros para encontrar clientes específicos."
        topRightAction={{
          label: 'Nuevo Cliente',
          onClick: openAddModal,
        }}
        getActions={getActions}
        filtersConfig={filterFields}
        onFilterChange={handleFilterChange}
        onResetFilters={handlerClearFilteredCustomers}
      />

      {/* Modal para registrar un nuevo cliente */}
      <CustomerRegistrationPopup
        isOpen={showRegistrationModal}
        onClose={closeAddModal}
        onSubmit={saveCustomer}
      />

      {/* Modal para editar cliente */}
      <CustomerEditPopup
        isOpen={showEditModal}
        onClose={closeEditModal}
        onSubmit={saveCustomer}
      />

      {/* Modal para ver detalle de cliente */}
      <CustomerViewPopup isOpen={showViewModal} onClose={closeViewModal} />
    </DefaultLayout>
  );
};

export default CustomerPage;
